import { Image, ImageViewer, Swiper } from "antd-mobile";
import Certificate from "components/alert/Certificate";
import dayjs from "dayjs";
import { disposeColors } from "pages/CarbonNeutralityDisclosure/config";
import { useEffect, useRef, useState } from "react";
import { colors, imgColors } from "../carbonNeutrality";

import * as echarts from "echarts";
import { truncateString } from "modules/common";
import r, { api } from "../../modules/Request";
import rightarrrowicon from "./../HomePage/images/rightarrow.png";
import CarbonDistribution from "./CarbonDistribution";
import EmissionDetails from "./EmissionDetails";
import Failure from "./Failure";
import rightOutline from "./img/RightOutline.png";
import dischargeIcon from "./img/discharge.png";
import gs from "./img/gs.png";
import left from "./img/left.png";
import titleIcon from "./img/titleIcon.png";
import zIcon from "./img/z-icon.png";
import ZitemICon from "./img/z-itemIcon.png";
import zjIcon from "./img/zjIcon.png";
import zsIcon from "./img/zs.png";
import "./index.scss";

function replaceSizeWithString(inputString) {
  const regex = /<font size="(\d+)">(.*?)<\/font>/g;
  const replacement = (_, size, content) => {
    const newSize = Math.ceil((parseInt(size) * 5.6) / 70);
    return `<font style='font-size: ${newSize}rem'>${content}</font>`;
  };

  const result = inputString.replace(regex, replacement);

  return result;
}

// 判断是否在微信环境
function isWeixinBrowser() {
  const ua = window.navigator.userAgent.toLowerCase();
  return ua.indexOf("micromessenger") !== -1;
}

/**
 * 组织碳排放组件
 * type === 1 碳中和 type===2 碳减排
 * @returns
 */
export const getOrganization = (dataVal, config = {}, type) => {
  const { style = {}, iconConfig = {} } = config;
  return (
    <div className="discharge-item" style={style}>
      <div
        className="item-icon"
        style={{
          backgroundImage: `url(${type === "1" ? ZitemICon : zjIcon})`,
        }}
      ></div>
      <div className="item-header">
        <div className="item-title">
          {type !== "1" ? (
            <img src={titleIcon} alt="" />
          ) : (
            <img src={zIcon} alt="" />
          )}
          <span style={{ color: type === "1" ? "#1677D0" : "#00a15e" }}>
            组织碳排放
          </span>
        </div>
      </div>
      <div className="boundary-item-content">
        <div className="content-title" style={{ color: "#333333" }}>
          核算依据
        </div>
        <div className="content">{dataVal?.calculateAccording || "--"}</div>
      </div>
      <div className="boundary-item-content" style={{ marginBottom: "0rem" }}>
        <div className="content-title">核算边界</div>
        <div className="content">
          <div className="boundary-item">
            <div className="boundary-item-hint">时间边界：</div>
            <div className="boundary-item-value">
              {dataVal?.emissionStartTime && dataVal?.emissionEndTime
                ? ` ${dayjs(dataVal?.emissionStartTime).format(
                    "YYYY年M月D日"
                  )}至${dayjs(dataVal?.emissionEndTime).format(
                    "YYYY年M月D日"
                  )}之间活动所产生的温室气体排放`
                : "--"}
            </div>
          </div>
          <div className="boundary-item">
            <div className="boundary-item-hint">组织边界：</div>
            <div className="boundary-item-value">{dataVal?.bound || "--"}</div>
          </div>
          <div className="boundary-item" style={{ marginBottom: "0rem" }}>
            <div className="boundary-item-hint">核算气体 边界：</div>
            <div className="boundary-item-value">
              {dataVal?.calculateGasBoundary || "--"}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * 碳排放披露
 * @returns
 */
export default function CarbonNeutralityDisclosure() {
  const ref = useRef(null);
  const chartPie = useRef(null);
  const chartRose = useRef(null);
  const [dataVal, setDataVal] = useState({});
  const [visible, setVisible] = useState(false);
  const [selectImgIndex, setSelectImgIndex] = useState(1);
  const [modalData, setModalData] = useState({
    open: false,
    imgList: [],
  });
  const colorsList = disposeColors(colors, imgColors, "2")?.colorList;
  // type:1 | 2  1是详情版本 2简介
  const { id, type } = r.getQueryString();
  useEffect(() => {
    getDetails(id);
    return () => {
      document.title = "上海探链科技有限公司";
    };
  }, []);

  useEffect(() => {
    const data = disposeCharData(dataVal?.pubEmissionCarbons);
    if (!chartPie.current || data?.pieList?.length === 0) return;
    // 创建图表实例
    const chart = echarts.init(chartPie.current);
    const maxValue = data?.pieList?.reduce((prev, current) => {
      return +prev.carbon > +current.carbon ? prev : current;
    });

    // // 准备数据和配置选项
    const options = {
      tooltip: false,
      grid: {
        containLabel: true, // 确保图表内容完全包含在容器中
        left: "0",
        right: "0",
        top: "0",
        bottom: "0",
      },
      legend: false,
      series: [
        {
          selectedMode: "single",
          radius: [40, 70],
          name: "Access From",
          type: "pie",
          labelLayout: function (params) {
            const isLeft = params.labelRect.x < chart.getWidth() / 2;
            const points = params.labelLinePoints;
            // Update the end point.
            points[2][0] = isLeft
              ? params.labelRect.x
              : params.labelRect.x + params.labelRect.width;
            return {
              labelLinePoints: points,
            };
          },
          data: data?.pieList,
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: "rgba(0, 0, 0, 0.5)",
            },
          },
          label: {
            alignTo: "edge",
            formatter: function (params) {
              const carbon = params?.data?.carbon;
              return [`{a|${carbon}%}`, `{b|${params.name}}`].join("\n");
            },
            minMargin: 5,
            edgeDistance: 10,
            lineHeight: 15,
            rich: {
              a: {
                // fontSize: '0.32rem',
                color: "#333333",
              },
              b: {
                // fontSize: '0.29rem',
                color: "#666666",
              },
            },
          },
        },
      ],
      graphic: [
        {
          type: "text",
          left: "center",
          top: "90px",
          style: {
            text: maxValue?.name,
            textAlign: "center",
            fill: "#666666",
            fontSize: 8,
          },
        },
        {
          type: "text",
          left: "center",
          top: "center",
          style: {
            text: `${maxValue?.carbon}%`,
            textAlign: "center",
            fill: "#333333",
            fontSize: 12,
            fontWeight: "bold",
          },
        },
      ],
    };

    // 监听点击事件
    chart.on("click", function (params) {
      if (params.seriesType === "pie") {
        chart.setOption({
          graphic: [
            {
              type: "text",
              left: "center",
              top: "90px",
              style: {
                text: params?.data?.name,
                textAlign: "center",
                fill: "#666666",
                fontSize: 8,
              },
            },
            {
              type: "text",
              left: "center",
              top: "center",
              style: {
                text: `${params?.data?.carbon}%`,
                textAlign: "center",
                fill: "#333333",
                fontSize: 12,
                fontWeight: "bold",
              },
            },
          ],
        });
      }
    });
    chart.setOption(options);
    // // 可选：响应式调整图表大小（如果需要自适应大小）
    window.addEventListener("resize", () => chart.resize());
  }, [dataVal]);

  useEffect(() => {
    const data = disposeCharData(dataVal?.pubEmissionCarbons);
    if (!chartPie.current) return;
    // 创建图表实例
    const chart = echarts.init(chartRose.current);
    // // 准备数据和配置选项
    const options = {
      series: [
        {
          name: "Radius Mode",
          type: "pie",
          radius: [30, 100],
          // center: ['25%', '50%'],
          roseType: "radius",
          itemStyle: {
            borderRadius: 10,
          },
          label: {
            show: false,
          },
          emphasis: {
            label: {
              show: true,
            },
          },
          data: data?.roseList,
        },
      ],
    };
    chart.setOption(options);
    // // 可选：响应式调整图表大小（如果需要自适应大小）
    window.addEventListener("resize", () => chart.resize());
  }, [dataVal]);

  /**
   * 微信分享
   */
  const weChatSharing = (data) => {
    const url = window.location.href;
    // eslint-disable-next-line no-undef
    wx?.ready(function () {
      //需在用户可能点击分享按钮前就先调用
      // eslint-disable-next-line no-undef
      wx?.updateAppMessageShareData({
        title: data?.data?.entName, // 分享标题
        desc: "碳排放披露", // 分享描述
        link: url, // 分享链接，该链接域名或路径必须与当前页面对应的公众号JS安全域名一致
        imgUrl: data?.data?.entLogoUrl, // 分享图标
        success: function () {
          // 设置成功
        },
        error: (err) => {
          console.log(err);
        },
      });

      // eslint-disable-next-line no-undef
      wx?.updateTimelineShareData({
        title: data?.data?.entName, // 分享标题
        link: url, // 分享链接，该链接域名或路径必须与当前页面对应的公众号JS安全域名一致
        imgUrl: data?.data?.entLogoUrl, // 分享图标
        success: function () {
          // 设置成功
        },
      });
    });
  };
  const getDetails = async (id) => {
    const result = await r.get(`${api.GET_FILLREVIEW}/${id}`);
    if (result.status.code === "00000") {
      document.title = result?.data?.entName;
      if (isWeixinBrowser()) {
        weChatSharing(result);
      }
      setDataVal(result?.data);
    } else {
      setDataVal({ failure: true });
    }
  };

  const disposeCharData = (data) => {
    console.log("--");
    const pieList = [];
    const roseList = [];
    data?.forEach((item, i) => {
      if (i === 0) {
        pieList.push({
          name: item?.name,
          value: +item?.ratio,
          carbon: item?.carbon,
          selected: true,
        });
      }
      pieList.push({
        name: item?.name,
        value: +item?.ratio,
        carbon: item?.carbon,
      });

      item?.children?.map((subItem) => {
        roseList.push({
          name: subItem?.name,
          value: +subItem?.ratio,
          carbon: item?.carbon,
        });
      });
    });
    return { pieList, roseList };
  };

  if (dataVal?.failure) {
    return <Failure />;
  }

  return (
    <div className="carbon-neutrality-disclosure-wrapper">
      <div className="company-content-wrapper">
        <div className="company-content">
          {dataVal?.entLogoUrl && <img src={dataVal?.entLogoUrl} alt="" />}
          <div className="company-title">{dataVal?.entName || "--"}</div>
          <div className="company-type">碳排放披露</div>
        </div>
        <div className="company-contents">
          <div
            className="discharge-data"
            style={{
              borderRadius: dataVal?.txid ? "0rem" : "0.21rem",
            }}
          >
            <div className="discharge-data-top">
              <div className="top-title">碳排放总量 (tCO₂e)</div>
              <div className="top-value">{dataVal?.emissionCarbon || "--"}</div>
            </div>
            <div className="discharge-data-button">
              数据周期：
              {dataVal?.emissionStartTime && dataVal?.emissionEndTime
                ? `${dayjs(dataVal?.emissionStartTime).format(
                    "YYYY/MM/DD"
                  )} - ${dayjs(dataVal?.emissionEndTime).format("YYYY/MM/DD")}`
                : "--"}
            </div>
          </div>
          {dataVal?.txid && (
            <div className="blockchain-content">
              <div className="blockchain-header">
                <div className="title">区块链存证</div>
                <div
                  className="options"
                  onClick={() => {
                    setVisible(true);
                  }}
                >
                  <span>查看证书</span>
                  <img src={left} className="options-img" alt="" />
                </div>
              </div>
              <div className="blockchain-values">
                <div className="detail_cz_box">
                  <p className="">Hash: </p>
                  <p className="detail_certificate_address">
                    {dataVal?.txid && truncateString(dataVal?.txid)}
                  </p>
                </div>
                <div
                  className="jump-box"
                  onClick={() => {
                    if (dataVal?.txidUrl) {
                      setVisible(true);
                    }
                    // dataVal?.txidUrl && window.open(dataVal?.txidUrl);
                  }}
                >
                  <img src={rightarrrowicon} alt="" />
                </div>
              </div>
            </div>
          )}
          {getOrganization({ ...dataVal }, {})}
          <CarbonDistribution dataVal={dataVal} type="2"></CarbonDistribution>

          {type !== "2" && dataVal?.detailList?.length ? (
            <div className="discharge-item">
              <div className="item-header">
                <div className="item-title">
                  <img src={titleIcon} alt="" />
                  <span>碳排放详情</span>
                </div>
                <div
                  className="item-icon"
                  style={{ backgroundImage: `url(${dischargeIcon})` }}
                >
                  <span>单位：tCO₂e</span>
                </div>
              </div>
              <div className="emission-details">
                <EmissionDetails data={dataVal?.detailList || []} type="2" />
              </div>
            </div>
          ) : (
            <></>
          )}
          {dataVal?.certificateName && dataVal?.certificateLogos?.length && (
            <div className="discharge-item">
              <div className="item-header">
                <div className="item-title ">
                  <img src={titleIcon} alt="" />
                  <span>{dataVal?.certificateName || "--"}</span>
                </div>
                <div
                  className="item-icon"
                  style={{ backgroundImage: `url(${zsIcon})` }}
                ></div>
              </div>
              <div className="certificate-img-list">
                <div className="img-list">
                  <Swiper
                    defaultIndex={0}
                    className={
                      dataVal?.certificateLogos?.length > 1
                        ? "swiper-colol"
                        : "swiper-empty-colol"
                    }
                    ref={ref}
                  >
                    {dataVal?.certificateLogos?.map((item, idx) => {
                      const colorIndex = idx % colorsList.length;
                      return (
                        <Swiper.Item key={item}>
                          <div
                            className="swiper-img"
                            style={{ borderColor: colorsList?.[colorIndex] }}
                          >
                            <img
                              src={item}
                              alt=""
                              onClick={(e) => {
                                const imgList = dataVal?.certificateLogos?.map(
                                  (item) => item
                                );
                                if (modalData?.open) return;
                                ImageViewer.Multi.show({
                                  images: imgList,
                                  defaultIndex: idx,
                                });
                              }}
                            />
                          </div>
                        </Swiper.Item>
                      );
                    })}
                  </Swiper>
                </div>
                {/* </div> */}
                {/* {dataVal?.certificateLogos?.length > 1 && (
                  <div className="img-options">
                    <div
                      className="left-icon"
                      style={{ opacity: selectImgIndex !== 1 ? 0.3 : 0 }}
                    >
                      {selectImgIndex !== 1 && (
                        <LeftOutline
                          color="#fff"
                          style={{
                            fontSize: "0.47rem",
                            color: "#fff",
                            fontWeight: "bold",
                          }}
                          onClick={() => {
                            if (selectImgIndex === 1) return;
                            ref.current?.swipePrev();
                            setSelectImgIndex(selectImgIndex - 1);
                          }}
                        />
                      )}
                    </div>
                    <div
                      className="right-icon"
                      style={{
                        opacity:
                          selectImgIndex !== dataVal?.certificateLogos?.length
                            ? 0.3
                            : 0,
                      }}
                    >
                      <RightOutline
                        color="#fff"
                        style={{
                          fontSize: "0.47rem",
                          color: "#fff",
                          fontWeight: "bold",
                        }}
                        onClick={() => {
                          if (
                            selectImgIndex === dataVal?.certificateLogos?.length
                          )
                            return;
                          ref.current?.swipeNext();
                          setSelectImgIndex(selectImgIndex + 1);
                        }}
                      />
                    </div>
                  </div>
                )} */}
              </div>
              {dataVal?.certificateOrgName && dataVal?.certificateLink && (
                <div
                  className="container-wrapper"
                  onClick={() => {
                    dataVal?.certificateLink &&
                      window.open(dataVal?.certificateLink);
                  }}
                >
                  <div class="container">
                    <div class="fixed-content">由 </div>
                    <div class="variable-content">
                      {dataVal?.certificateOrgName}
                    </div>
                    <div className="container-text">颁发，查看详情</div>
                    <div className="container-icon">
                      <img src={rightOutline} alt="" />
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          {dataVal?.entInfo && (
            <div className="discharge-item">
              <div className="item-header">
                <div className="item-title">
                  <img src={titleIcon} alt="" />
                  <span>关于企业</span>
                </div>
                <div
                  className="item-icon"
                  style={{ backgroundImage: `url(${gs})` }}
                ></div>
              </div>
              <div className="gs-content">
                {dataVal?.entInfo && (
                  <div
                    dangerouslySetInnerHTML={{
                      __html: replaceSizeWithString(dataVal?.entInfo),
                    }}
                  ></div>
                )}
              </div>
            </div>
          )}
        </div>
        {dataVal?.footLogo && (
          <div className="data_center_logo">
            <Image src={dataVal?.footLogo} />
          </div>
        )}
        {/*<div className="signature-value">*/}
        {/*  <div className="signature-logo"></div>*/}
        {/*</div>*/}
      </div>
      <Certificate
        visible={visible}
        setVisible={setVisible}
        list={{
          certificateContent: dataVal?.certificateContent,
          certificateAddress: dataVal?.txidUrl,
          blockHeight: dataVal?.blockNumber,
          certificateTime: dataVal?.certificateTime,
          certificateSubjects: dataVal?.certificateSubjects,
          geoLocation: dataVal?.geoLocation,
        }}
      />

      {modalData?.imgList?.length ? (
        <ImageViewer.Multi
          images={modalData?.imgList}
          visible={modalData?.open}
          onClose={(e) => {
            setModalData({
              open: false,
              imgList: [],
            });
          }}
        />
      ) : (
        ""
      )}
    </div>
  );
}
